import { ValueOption } from ".";


/**
 * Represents an object for describing the supported values 
 * whether they be inputs or outputs for a deal type.
 * 
 * Currently, 23 Jan 2025
 * Values that are stored for the deal (rates) are stored in a dealitem that is created by default and linked to the deal with no tower assignment.
 * This works where we have a single deal type for the deal (Which is created from a deal group).
 * 
 * Consequently, if the deal group supports multiple deal types then we could potentially have the same value (code) being requested multiple times. 
 * i.e. country that we are contracting from.
 * 
 * Within the UI we show this default deal item as if it represents the deal, ideally we would like to show those values for collection here
 * When we add a tower to the deal, we create a deal item for the tower and link it to the deal item for the deal.
 * 
 * The tower is associated with a deal type and its own set of values that will need collection.
 * 
 * A tower for the rates deal type it is envisaged would share the same values across all towers for that deal type - contracted from country, percentile range.
 * So if the deal type requests country, percentile range. Then we would like to see the two values only showing for collection at the overal deal level.
 * 
 * The issue we have then is when we mix towers with different deal types.
 * In this case we could have:
 * Rates deal type - Country, Percentile Range
 * Networks deal type - Country, Network Type
 * 
 * In our deal item for the deal we would like to see the following values for collection: Country.
 * 
 * Clicking on Networks would show the collection screen for just
 * Network Type
 * 
 * Issue we have with Rates is that we do not show a collection screen. 
 * Hence, Percentile Range also needs to be added to the main default screen. 
 * From inferrment, all values for a rates deal type must be added to the main screen.
 * 
 * This then moves the decision for what values to show and where to the UI and consequently not try to enforce it here with dealgroup possible assignment.
 * 
 * After all this lets consider storage. To make life simpler and in the case of tower deletions. 
 * Common values collected at the 'deal level' should now be set across all deal items in the deal.
 * Note: although we have matched common values by their codes 
 * - we will need to assign the correct value identifier that exists for the deal type that the dealitem is associated with.
 * 
 * I.e.
 * Rates deal type - Country Contracted from : code 'ctry-contract', id 'abc' - should be used for pb_dealValue as the deal value
 * Networks deal type - Country Contracted from : code 'ctry-contract', id 'def' - should be used for pb_dealValue as the deal value
 * 
 * Similar lookup resolution will also be required for the value options.
 * 
 */
export class ModelValue {
  /**
   * Gets or sets the unique identifier of the model value.
   */
  id?: string | null;

  /**
   * Gets or sets the parent identifier of the model value.
   * Used for hierarchical structures, 
   * i.e. a model value can be a child of another model value.
   * or a model value could be a container/section for others.
   */
  parentId?: string | null;

  /**
   * Gets or sets the deal group identifier that this would be associated with. 
   */
  dealGroupId?: string | null;

  /**
   * Gets or sets the deal type identifier that this would be associated with. 
   */
  dealTypeId?: string | null;

  /**
   * Gets or sets the code for the type of data to be stored.
   */
  dataTypeCode?: string | null;

  /**
   * Gets or sets the code for the control type that will be used for rendering in the UI.
   * Required for input values.
   */
  controlCode?: string | null;

  /**
   * Optional. Gets or sets the units that will be used.
   * The underlying storage should not be adjusted.
   */
  unitCode?: string | null;

  /**
   * Optional. Gets or sets the display text override for the units.
   */
  unitDisplay?: string | null;

  /**
   * Gets or sets the unique code to identify the model value, within a deal type.
   * Codes may be reused across deal types or when assignment has been made also to a deal group.
   */
  code?: string | null;

  /**
   * Gets or sets a code that can be used elsewhere to match and map values across.
   */
  codeMap?: string | null;

  /**
  * Used with code map to determine if the value assignment will be used as part of updating the parent deal header data 
  * or when changes in deal header values are made that those values are written to this record when the parent model value code matches the code map specified.
  */
  updatesHeader?: boolean | null;

  /**
  * Gets or sets a value indicating whether the model value supports time segments.
  * Some values may be time dependent, i.e. volumes may change over time
  * Time segments are to be determined by usage, most often being each segment represents a year
  */
  supportsTimeSegments?: boolean | null;

  /**
   * Gets or sets if the a permission check is required to display the model value.
   */
  permissionCheck?: boolean | null;

  /**
   * Gets or sets the display text. This may be the question text or the output text.
   */
  displayText?: string | null;

  /**
   * Gets or sets if we should apply formatting/value substitution for the display text.
   */
  useDisplayFormat?: boolean | null;

  /**
   * Gets or sets the display sort order for the value.
   */
  sortOrder?: number | null;

  /**
   * Gets or sets a value indicating whether the value assignment comes from an option list.
   * Required for control types that support lists.
   */
  hasOptions?: boolean | null;

  /**
   * Gets or sets a value indicating whether the config supports storage of option cascade values.
   */
  hasOptionCascade?: boolean | null;

  /**
   * Gets or sets a value indicating whether this config has dependencies with others identified by varName.
   */
  hasDependencies?: boolean | null;

  /**
   * Gets or sets a value indicating whether this config has an additional chart config.
   */
  hasChart?: boolean | null;

  /**
   * Gets or sets the variable name that will be used within the UI.
   * Required for Inputs.
   */
  varName?: string | null;

  /**
   * Gets or sets the variable name used that will be used to lookup data.
   * Required when isVirtual is true.
   */
  varNameVirt?: string | null;

  /**
   * Gets or sets a format string for value display.
   */
  formatValue?: string | null;
  
  /**
   * Optional. Gets or sets a default value.
   */
  defaultValue?: string | null;

  /**
   * Optional. Gets or sets the placeholder text in the UI.
   */
  placeholder?: string | null;

  /**
   * Optional. Gets or sets any additional help, comments.
   */
  helpComments?: string | null;

  /**
   * Gets or sets a value indicating whether the model value is an input or not.
   */
  isInput?: boolean | null;

  /**
   * Gets or sets a value indicating whether the model value is used for a report.
   */
  isReport?: boolean | null;

  /**
   * Gets or sets a value indicating whether the output value should be displayed to the user.
   */
  showToUser?: boolean | null;

  /**
   * Gets or sets a value indicating whether changes to the value will affect calculations.
   */
  isPriceDriver?: boolean | null;

  /**
   * Gets or sets a value indicating whether the model value is required 
   * to be set before doing any further processing
   */
  isRequired?: boolean | null;

  /**
   * Gets or sets a value indicating whether the model value is initialized and then made readonly.
   * Some fields like the creation date or user that created the value may not be editable after initialization.
   */
  isReadOnly?: boolean | null;

  /**
   * Gets or sets a value indicating whether the value is just a store for metadata.
   * Could also be structural, i.e. a header / container for other values.
   */
  isMetaData?: boolean | null;

  /**
   * Gets or sets a value indicating whether the model value is virtual.
   * It will not store deal value data but should have its varName used
   * to lookup values from other sources
   */
  isVirtual?: boolean | null;

  /**
   * Gets or sets a value indicating whether the value is active.
   * Over the course of time, values may be deactivated.
   */
  isActive?: boolean | null;

  /**
   * Gets or sets a value indicating the display state for custom adjustment processing.
   * By default, null or zero would show the value incl. custom adjustment if it exists, otherwise the original value.
   * A value of -1 would show the difference if available or null.
   *
   * A value of 1 would show the original value only if we have an existing valid custom adjustment.
   * This allows us to add a config row that displays only when custom adjustments are being calculated.
   * i.e. if we have ca values then it will show numbers, if we dont have ca all the values will be null and using remove empty rows will then stop the row from displaying.
   * 
   */
  caState?: number | null;

  /**
   * Gets or sets a value indicating whether the model value has Excel support.
   * This is for values that can be exported/imported to Excel.
   */
  hasExcel?: boolean | null;

  /**
   * Gets or sets the Excel address for the value.
   */
  excelAddress?: string | null;

  /**
   * Gets or sets the row offset/repeat used for retrieving multi year data from Excel.
   */
  excelRepeatRow?: number | null;

  /**
   * Gets or sets the column offset/repeat used for retrieving multi year data from Excel.
   */
  excelRepeatCol?: number | null;

  /**
   * Gets or sets the starting time segment minimum value i.e. some model values start from year 2 i.e. Difference values.
   */
  timeSegmentMin?: number | null;

  /**
   * Gets or sets the time segment maximum value - we may have model values that are calculated independent of the duration and have a fixed period upper length.
   */
  timeSegmentMax?: number | null;

  /**
 * Gets or sets if we should repeat the first value across the duration range.
 */
  timeSegmentRepeatValue?: boolean | null;

  /**
   * Gets or sets a row starting point for grid data. I.e. to accomodate row headers
   */
  gridRowStart?: number | null;

  /**
  * Column to determine if the row has valid grid data
  */
  gridColValid?: string | null;

  /**
   * Gets or sets a value indicating whether empty rows are excluded from the grid output
   */
  gridExcludeEmptyRow?: boolean | null;

  /**
   * Gets or sets a value indicating whether we should create multiple output grids to display data for each time segment
   */
  gridByYear?: boolean | null;

  /**
   * Gets or sets a value indicating whether we should create multiple output grids to display data for each row.
   */
  gridByRow?: boolean | null;

  /**
   * Gets or sets a value indicating whether we should use the useScenarioData for result section .
   */
  useScenarioData?: boolean | null;

  /**
   * Gets or sets a value indicating whether the model value can be overridden.
   */
  override?: boolean | null;

  /**
   * Gets or sets a formula calculation. 
   */
  formula?: string | null;

  /**
   * Gets or sets if we should process any value formula calculations in a specific order.
   * Note: Duplicate values i.e. all one would be done without any order
   * , if a value existed with a value of two then it would be done afterwards.
   */
  formulaOrder?: number | null;

  /**
   * Optional. Gets or sets the minimum value.
   */
  min?: number | null;

  /**
   * Optional. Gets or sets the maximum value.
   */
  max?: number | null;

  /**
   * Optional. Gets or sets the data size.
   */
  size?: number | null;

  /**
   * Optional. Gets or sets the SQL option link.
   * Not implemented - advanced, future feature for linking to a SQL table for options.
   * Has the potential to be used for dynamic lists, however, at a cost of performance.
   */
  sqlOptionLink?: string | null;

  /**
   * Optional. Gets or sets the SQL option lookup function.
   * Not implemented - advanced, future feature for linking to a SQL table for options.
   * Has the potential to be used for dynamic lists, however, at a cost of performance.
   */
  sqlOptionLookUpFn?: string | null;

  /**
   * Optional. Allows us to potentially identify items in the DOM. This is an addition to
   * any existing classes applied by the component.
   */
  cssClass?: string | null;

  /**
   * Gets or sets the value creation date and time.
   */
  createdOn?: Date | null;

  /**
   * Gets or sets the creator's name.
   */
  createdBy?: string | null;

  /**
   * Gets or sets the last modification date and time.
   */
  lastModifiedOn?: Date | null;

  /**
   * Gets or sets the name of the person who last modified the model value.
   */
  lastModifiedBy?: string | null;

  /**
   * Optional. Gets or sets the categories that the model value belongs to.
   * This is for grouping values together.
   */
  categories?: string[] | null;

}
